import "./rangepicker.scss";

import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Datepicker, formatDate, Input, Popup, Select } from "@mobiscroll/react";
import PropTypes from "prop-types";

import { Button } from "@/ui-lib";
import { getCompanyDateFormat2Digit } from "@/utils/date";

import { DEFAULT_RANGE_OPTIONS } from "./constants";

import styles from "./RangeDatepickerV2.module.scss";

const RangeDatepickerV2 = forwardRef(
  (
    {
      className = "",
      placeholder = "",
      dateFormat = getCompanyDateFormat2Digit(),
      rangeOptions = DEFAULT_RANGE_OPTIONS,
      defaultRangeOption = "custom",
      onChange = null,
      onOpen = null,
      onClose = null,
      isOpen: controlledIsOpen = undefined,
      onIsOpenChange,
      value,
      onApply = () => {},
      autoApply = false,
      ...props
    },
    ref
  ) => {
    const [localIsOpen, setLocalIsOpen] = useState(false);
    const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : localIsOpen;

    const [internalDates, setInternalDates] = useState(["", ""]);
    const selectedDate = value || internalDates;

    const [rangeOption, setRangeOption] = useState(defaultRangeOption);
    const [inputValue, setInputValue] = useState("");

    const inputEl = useRef(null);

    const getPlatform = () => {
      if (navigator.userAgentData) {
        return navigator.userAgentData.platform;
      }

      return navigator.platform;
    };

    const isWindows = getPlatform()?.toLowerCase()?.includes("win");

    const handleOpen = useCallback(() => {
      if (typeof onOpen === "function") {
        onOpen();
      }

      if (controlledIsOpen === undefined) {
        setLocalIsOpen(true);
      } else {
        onIsOpenChange?.(true);
      }
    }, [onOpen, controlledIsOpen, onIsOpenChange]);

    const handleClose = useCallback(() => {
      if (typeof onClose === "function") {
        onClose();
      }

      if (controlledIsOpen === undefined) {
        setLocalIsOpen(false);
      } else {
        onIsOpenChange?.(false);
      }
    }, [onClose, controlledIsOpen, onIsOpenChange]);

    const formatInputValue = useCallback(
      (startLocal, endLocal) => {
        if (startLocal && endLocal) {
          return `${formatDate(dateFormat, new Date(startLocal))} - ${formatDate(
            dateFormat,
            new Date(endLocal)
          )}`;
        }

        return "";
      },
      [dateFormat]
    );

    const handleApply = useCallback(() => {
      if (Array.isArray(selectedDate)) {
        const [start, end] = selectedDate;
        setInputValue(formatInputValue(start, end || start));

        if (typeof onChange === "function") {
          onChange({ startDate: start, endDate: end || start });
        }

        if (typeof onApply === "function") {
          onApply();
        }
      }

      handleClose();
    }, [selectedDate, formatInputValue, onChange, handleClose]);

    const handleCancel = useCallback(() => {
      handleClose();
    }, [handleClose]);

    const onDateChange = useCallback(
      (ev) => {
        const newValue = ev.value;
        setRangeOption("custom");

        if (!value) {
          setInternalDates(newValue);
        }

        if (typeof onChange === "function") {
          onChange({
            startDate: newValue[0],
            endDate: newValue[1],
          });
        }
      },
      [value]
    );

    const getRangeDates = useCallback((optionValue) => {
      const now = new Date();
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      const shiftDate = (date, days) => {
        const d = new Date(date);
        d.setDate(d.getDate() + days);
        return d;
      };

      let rangeStart = startOfToday;
      let rangeEnd = startOfToday;

      switch (optionValue) {
        case "today":
          rangeStart = startOfToday;
          rangeEnd = startOfToday;
          break;
        case "yesterday":
          rangeStart = shiftDate(startOfToday, -1);
          rangeEnd = shiftDate(startOfToday, -1);
          break;
        case "last-week":
          rangeStart = shiftDate(startOfToday, -7);
          rangeEnd = shiftDate(startOfToday, -1);
          break;
        case "last-month":
          rangeStart = shiftDate(startOfToday, -30);
          rangeEnd = shiftDate(startOfToday, -1);
          break;
        case "last-7-days":
          rangeStart = shiftDate(startOfToday, -6);
          break;
        case "last-30-days":
          rangeStart = shiftDate(startOfToday, -29);
          break;
        default:
          break;
      }

      return [rangeStart, rangeEnd];
    }, []);

    const handleSelectChange = useCallback(
      (ev) => {
        const { value: optionValue } = ev;
        setRangeOption(optionValue);

        if (optionValue !== "custom") {
          const [start, end] = getRangeDates(optionValue);

          if (!value) {
            setInternalDates([start, end]);
          }

          setInputValue(formatInputValue(start, end));

          if (typeof onChange === "function") {
            onChange({ startDate: start, endDate: end });
          }
        }
      },
      [setRangeOption, getRangeDates, value, formatInputValue, onChange]
    );

    const respPopup = useMemo(
      () => ({
        xsmall: {
          display: "bottom",
          touchUi: true,
          buttons: [
            {
              text: "Apply",
              handler: () => handleApply(),
            },
            "cancel",
          ],
        },
        custom: {
          breakpoint: 559,
          buttons: [],
          display: "anchored",
          anchor: inputEl.current?.nativeElement,
          anchorAlign: "start",
          touchUi: false,
          scrollLock: false,
          showArrow: false,
          maxWidth: 920,
        },
      }),
      [handleApply, inputEl]
    );

    useEffect(() => {
      if (value && Array.isArray(value) && value[0] && value[1]) {
        const [start, end] = value;
        setInputValue(formatInputValue(start, end));
      }
    }, [value, formatInputValue]);

    return (
      <div className={className} ref={ref}>
        <Input
          ref={inputEl}
          themeVariant="light"
          theme="ios"
          onClick={handleOpen}
          value={inputValue}
          placeholder={placeholder}
          readOnly
          className={styles.calendarInput}
        />

        <Popup
          themeVariant="light"
          theme="ios"
          isOpen={isOpen}
          onClose={handleClose}
          responsive={respPopup}
          position="top"
          cssClass="rangepicker-popup"
        >
          <div className={styles.container}>
            <div className={styles.datePickerContainer}>
              <Datepicker
                select="range"
                display="inline"
                themeVariant="light"
                theme="ios"
                pages="auto"
                showOnClick={false}
                showOnFocus={false}
                value={selectedDate}
                onChange={onDateChange}
                dateFormat={dateFormat}
                {...props}
              />
            </div>

            <div className={styles.filtersContainer}>
              <Select
                themeVariant="light"
                theme="ios"
                data={rangeOptions}
                label="Date range"
                labelStyle="stacked"
                inputStyle="box"
                value={rangeOption}
                onChange={handleSelectChange}
                className={styles.dateRangeSelect}
                cssClass={isWindows ? "rangepicker-select" : ""}
              />
              {!autoApply && (
                <div className={styles.buttonsContainer}>
                  <Button className={styles.applyButton} onClick={handleApply}>
                    Apply
                  </Button>
                  <Button className={styles.cancelButton} outline onClick={handleCancel}>
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Popup>
      </div>
    );
  }
);

RangeDatepickerV2.propTypes = {
  className: PropTypes.string,
  placeholder: PropTypes.string,
  dateFormat: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])),
  rangeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      text: PropTypes.string,
    })
  ),
  defaultRangeOption: PropTypes.string,
  onChange: PropTypes.func,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  isOpen: PropTypes.bool,
  onIsOpenChange: PropTypes.func,
  onApply: PropTypes.func,
};

export default RangeDatepickerV2;
